"use client";

import { ActionButton } from "../ActionButton/ActionButton";
import { FeaturedMedia } from "../FeaturedMedia/FeaturedMedia";
import { FeaturedPerson } from "../FeaturedMedia/FeaturedPerson";
import { SectionIntro } from "../SectionIntro/SectionIntro";
import classNames from "classnames/bind";
import useEmblaCarousel from "embla-carousel-react";
import { WheelGesturesPlugin } from "embla-carousel-wheel-gestures";
import { ReactNode, useCallback, useEffect, useState } from "react";
import styles from "./GraphicCardCarousel.module.css";

const cx = classNames.bind(styles);

const CARD_GRAPHICS = {
  circle: "/graphic-card-carousel/circle-graphic.png",
  curves: "/graphic-card-carousel/curves-graphic.png",
  "horizontal-lines": "/graphic-card-carousel/horizontal-lines-graphic.png",
  starburst: "/graphic-card-carousel/starburst-graphic.png",
  zigzags: "/graphic-card-carousel/zigzags-graphic.png",
} as const;

export type CardGraphic = keyof typeof CARD_GRAPHICS;

interface GraphicCard {
  label: string;
  copy?: ReactNode;
  cardGraphic?: CardGraphic | null;
}

interface GraphicCardCarouselProps {
  kicker?: string;
  headline: string;
  copy?: ReactNode;
  cards: GraphicCard[];
  media?: {
    subheadline?: string | null;
    mediaType?: "image" | "video" | null;
    image?: {
      src: string;
      alt: string;
    } | null;
    videoPlaybackId?: string | null;
    videoTitle?: string | null;
    videoThumbnailTimestamp?: number | null;
    featuredPerson?: {
      name?: string | null;
      jobTitle?: string | null;
      image?: {
        src: string;
        alt: string;
      };
    };
  };
}

export const GraphicCardCarousel = ({
  kicker,
  headline,
  copy,
  cards,
  media,
}: GraphicCardCarouselProps) => {
  const [navState, setNavState] = useState({
    prev: false,
    next: false,
  });
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      align: "start",
      containScroll: "trimSnaps",
      loop: false,
      watchDrag: true,
      dragFree: true,
    },
    [WheelGesturesPlugin({ forceWheelAxis: "x" })],
  );

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setNavState({
      prev: emblaApi.canScrollPrev(),
      next: emblaApi.canScrollNext(),
    });
  }, [emblaApi]);

  const scrollPrev = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollNext();
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);

    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi, onSelect]);

  if (!cards?.length) {
    return null;
  }

  return (
    <section className={cx("section", "graphic-card-carousel")}>
      <div className={cx("container")}>
        <div className={cx("inner")}>
          <div className={cx("intro")}>
            <SectionIntro variant="split" kicker={kicker} headline={headline}>
              {copy}
            </SectionIntro>
          </div>
          {cards.length && (
            <>
              <div className={cx("embla-shell")}>
                <div className={cx("embla")}>
                  <div className={cx("embla-viewport")} ref={emblaRef}>
                    <div className={cx("embla-container")}>
                      {cards.map((card, index) => {
                        const graphicSrc = card.cardGraphic
                          ? CARD_GRAPHICS[card.cardGraphic]
                          : undefined;

                        return (
                          <div
                            key={`graphic-card-${index}`}
                            className={cx("embla-slide")}
                          >
                            <article className={cx("card")}>
                              {graphicSrc ? (
                                <img
                                  className={cx("card-graphic")}
                                  src={graphicSrc}
                                  alt=""
                                />
                              ) : null}
                              <div className={cx("card-content")}>
                                <h3 className={cx("card-title")}>
                                  {card.label}
                                </h3>
                                {card.copy ? (
                                  <div className={cx("card-copy")}>
                                    {card.copy}
                                  </div>
                                ) : null}
                              </div>
                            </article>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
              <div className={cx("navigation")}>
                <ActionButton
                  size="medium"
                  variant="dark"
                  onClick={scrollPrev}
                  disabled={!navState.prev}
                  aria-label="Previous card"
                >
                  Previous
                </ActionButton>
                <ActionButton
                  size="medium"
                  variant="dark"
                  onClick={scrollNext}
                  disabled={!navState.next}
                  aria-label="Next card"
                >
                  Next
                </ActionButton>
              </div>
            </>
          )}
          {media ? (
            <div>
              {media.subheadline ? (
                <h3 className={cx("subheadline", "text-h3")}>
                  {media.subheadline}
                </h3>
              ) : null}
              <FeaturedMedia {...media}>
                <FeaturedPerson
                  {...media.featuredPerson}
                  className={cx("featured-person-desktop")}
                />
              </FeaturedMedia>
              <FeaturedPerson
                {...media.featuredPerson}
                className={cx("featured-person-mobile")}
              />
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
};
